import { useCallback, useState } from 'react';
import { useToast } from 'hooks/useToast';
import { useAppSelector, useAppDispatch } from 'store/hooks/useStore';
import {
  listSelectors,
  listActions,
} from 'store/reducers/menuCategoryReducer/listSlice';
import * as menuCategoryApi from 'api/menuCategoryApi';
import { MenuCategory } from 'types/menuCategory';
import { arrayMove } from '@dnd-kit/sortable';
import { DragEndEvent } from '@dnd-kit/core';
import { useListStore } from './useListStore';

/**
 * メニューカテゴリー並び替え store の実装をコンポーネントから隠すための中間層
 * @returns selector と action のラッパー
 */
export const useSortStore = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const { fetchMenuCategories } = useListStore();
  const menuCategories = useAppSelector(listSelectors.filteredMenuCategories);
  const [sortedMenuCategoryId, setSortedMenuCategoryId] = useState<
    MenuCategory['id'] | null
  >(null);
  const [accordionOpenedIds, setAccordionOpenedIds] = useState<number[]>([]);
  const accordionIndex = accordionOpenedIds.map((openedId) =>
    menuCategories.findIndex(({ id }) => id === openedId),
  );

  const onDragEndMenuCategories = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over?.id && active.id !== over?.id) {
        const oldIndex = menuCategories.findIndex(({ id }) => id === active.id);
        const newIndex = menuCategories.findIndex(({ id }) => id === over.id);
        const movedMenuCategories = arrayMove(
          menuCategories,
          oldIndex,
          newIndex,
        ).map((item, idx) => ({
          ...item,
          displayOrder: idx + 1,
        }));

        dispatch(
          listActions.fetchMenuCategoriesSuccess({
            menuCategories: movedMenuCategories,
          }),
        );

        setSortedMenuCategoryId(active.id as number);
      }
    },
    [menuCategories, dispatch],
  );

  const checkOpenStateSortPopover = useCallback(
    (id: MenuCategory['id']) => sortedMenuCategoryId === id,
    [sortedMenuCategoryId],
  );

  const onCancelSort = useCallback(() => {
    setSortedMenuCategoryId(null);
    void fetchMenuCategories();
  }, [fetchMenuCategories]);

  const onSort = useCallback(() => {
    const payload = menuCategories.map(({ id }, idx) => ({
      id,
      displayOrder: idx + 1,
    }));
    void (async () => {
      const result = await menuCategoryApi.editMenuCategoryOrder(payload);
      if (result.ok) {
        toast({
          status: 'success',
          description: `カテゴリーの順序を並び替えました。`,
        });
      } else {
        toast({
          status: 'error',
          description: `カテゴリーの並び替えに失敗しました: ${result.err.message}`,
        });
      }
      setSortedMenuCategoryId(null);
      void fetchMenuCategories();
    })();
  }, [menuCategories, toast, fetchMenuCategories]);

  const onChangeAccordion = useCallback(
    (index: number[]) => {
      const ids = index.map((idx) => menuCategories[idx].id);
      setAccordionOpenedIds(ids);
    },
    [menuCategories],
  );

  return {
    /** メニューカテゴリーのアコーディオンの開閉状態 */
    accordionIndex,
    /** メニューカテゴリーのアコーディオンの開閉時の処理 */
    onChangeAccordion,
    /** メニューカテゴリーの並び替え */
    onDragEndMenuCategories,
    /** 並び替え実行前の確認 popover 表示するかチェックする */
    checkOpenStateSortPopover,
    /** 並び替え実行キャンセル */
    onCancelSort,
    /** 並び替え実行 */
    onSort,
  };
};
